import { WRAPPED_NATIVE_TOKEN_BY_CHAIN } from '@cronos-app/binding-swap-sdk';
import type { ChainId_L1 } from '@cronos-app/sdk';
import { ChainId, ChainLayer, getChainOfLayer } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Fraction } from 'bi-fraction';
import { ZeroAddress } from 'ethers';
import { useMemo } from 'react';
import { create } from 'zustand';

import type { ExtendedTokenInfo } from '../types';
import { useCardLayer } from '../useCardContextStore';

/**
 * @param token
 * @returns
 */
export const useTokenUsdPriceByService = (token: ExtendedTokenInfo) => {
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const hardcode = usePriceStore((state) => state.prices);
  return useQuery({
    queryKey: getUseTokenUsdPriceByServiceKey(network, token, layer, hardcode),
    queryFn: async () =>
      getTokenUsdPriceByService(layer, network, token, hardcode),
    refetchInterval: 60_000,
    retry: false,
  }).data;
};

export const useTokenUsdPricesByService = (tokens: ExtendedTokenInfo[]) => {
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const hardcode = usePriceStore((state) => state.prices);
  const ans = useQueries({
    queries: tokens.map((token) => ({
      queryKey: getUseTokenUsdPriceByServiceKey(
        network,
        token,
        layer,
        hardcode,
      ),
      queryFn: async () =>
        getTokenUsdPriceByService(layer, network, token, hardcode),
      refetchInterval: 60_000,
      retry: false,
    })),
  });

  return useMemo(() => {
    const record: Record<string, string | undefined> = {};
    ans.forEach((item, idx) => {
      if (!item.data) return;
      record[tokens[idx].address] = item.data.toFixed(18);
    });
    return record;
  }, [ans, tokens]);
};

const getUseTokenUsdPriceByServiceKey = (
  network: ChainId_L1,
  token: ExtendedTokenInfo,
  layer: ChainLayer,
  hardcode?: Record<string, string | undefined>,
) => ['useTokenUsdPriceByService', network, token, layer, hardcode];

const getTokenUsdPriceByService = async (
  layer: ChainLayer,
  network: ChainId_L1,
  token: ExtendedTokenInfo,
  hardcode?: Record<string, string | undefined>,
) => {
  const t = hardcode?.[token.symbol];
  if (t) return new Fraction(t);

  // try {
  //   // from cdc price service
  //   const r1 = await tokenFetcher(
  //     {
  //       type: Token.Unknown,
  //       address: token.address,
  //       layer,
  //     },
  //     network,
  //   );
  //   if (r1?.usd_price) return r1.usd_price;
  // } catch (e) {
  //   //
  // }
  const endpoint = getPriceEndpoint(layer, network, token.address);
  if (!endpoint) return null;
  try {
    const r2: {
      data: {
        priceUSD: number;
      };
    } = await fetch(endpoint).then((r) => r.json());
    return new Fraction(r2.data.priceUSD);
  } catch (e) {
    return null;
  }
};

const getPriceEndpoint = (
  layer: ChainLayer,
  network: ChainId_L1,
  tokenAddress: string,
) => {
  const wrappedAddr =
    tokenAddress === ZeroAddress
      ? // eslint-disable-next-line
        // @ts-expect-error
        WRAPPED_NATIVE_TOKEN_BY_CHAIN[+getChainOfLayer(network, layer)]?.address
      : tokenAddress;
  const subpath = wrappedAddr.toLowerCase();
  if (!subpath) return;
  // NOTE: always use mainnet for price
  if (layer === ChainLayer.L2) {
    if (network === ChainId.ETHEREUM)
      return `https://api.h2.finance/general/api/v1/token/${subpath}/price`;
    return `https://psta-h2-api.crorc.co/api/v1/token/${subpath}/price `;
  }
  if (network === ChainId.ETHEREUM)
    return `https://api.vvs.finance/general/api/v1/token/${subpath}/price`;
  return `https://psta-vvs-api.crorc.co/api/v1/token/${subpath}/price`;
};

export const usePriceStore = create<{
  prices: Record<string, string | undefined>;
  setPrice: (symbol: string, price: string) => void;
}>((set) => ({
  prices: {},
  setPrice: (symbol, price) =>
    set((state) => ({ prices: { ...state.prices, [symbol]: price } })),
}));
if (typeof window !== 'undefined' && process.env.APP_ENV !== 'prod') {
  // eslint-disable-next-line
  (window as any).setPrice = usePriceStore.getState().setPrice;
}
